/**
 * Single source of truth for invoking a tool's `message.result` formatter.
 *
 * The formatter has an `IsProducer`-conditional union signature
 * (`{args, dataset}` for producers, `{args, output}` for non-producers — see
 * {@link ToolMessageFormatters}). Two consumers need to dispatch over that
 * union and call the formatter:
 *
 * - `ToolResultDetails` (`components/tool-display/ToolStatus.tsx`) — renders
 *   the formatter output as JSX in the "Show details" panel.
 * - `useRerunTool` (`hooks/useRerunTool.ts`) — logs the rendered prose to
 *   the console as a debug affordance.
 *
 * Both call into here so the producer-detection heuristic and the
 * union-collapsing cast cannot drift between the UI and the debug path.
 */

import type { ReactNode } from 'react';

import type { ErasedToolMessageFormatters } from './message-formatters.ts';

/**
 * Invoke a tool's `message.result` formatter with the `{ args, output }`
 * input shape. The same dispatch powers `ToolResultDetails` (UI) and
 * `useRerunTool` (debug), so the two consumer sites cannot drift.
 *
 * Returns `undefined` when no formatter is registered. Returns whatever
 * the formatter returns otherwise (string, ReactNode, or null).
 */
export function callResultFormatter(
  message: ErasedToolMessageFormatters | undefined,
  args: Record<string, unknown>,
  result: unknown,
): string | ReactNode | null | undefined {
  if (!message?.result) {
    return undefined;
  }
  return message.result({ args, output: result });
}
